/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Weapons domain - Weapon type configurations and combat mechanics
 * weapons.config.ts - All weapon-related configuration constants and parameters
 */

// ============================================================================
// WEAPON TYPE DEFINITIONS
// ============================================================================

/**
 * Available weapon types in the game
 * Each weapon has distinct characteristics for different playstyles
 */
export enum WeaponType {
  /** Default balanced weapon - reliable single-shot precision */
  DEFAULT = "DEFAULT",
  /** Close-range spread weapon - multiple pellets with wide arc */
  SHOTGUN = "SHOTGUN",
  /** Long-range precision weapon - high damage, slow fire rate */
  SNIPER = "SNIPER",
  /** Rapid-fire weapon - sustained damage with moderate spread */
  MACHINEGUN = "MACHINEGUN",
}

// ============================================================================
// WEAPON STATISTICS INTERFACE
// ============================================================================

/**
 * Complete weapon configuration defining combat behavior
 * All weapons are balanced to similar DPS (~10-12.5) with different mechanics
 */
export interface WeaponStats {
  /** Base damage per bullet (before player scaling) */
  readonly damage: number;
  /** Cooldown between shots in milliseconds */
  readonly fireRateMs: number;
  /** Bullet velocity in pixels per second */
  readonly bulletSpeed: number;
  /** Bullet duration in seconds before despawning */
  readonly bulletLifetime: number;
  /** Bullet spread angle in degrees (0 = precise, >0 = spread cone) */
  readonly spreadDegrees: number;
  /** Number of bullets fired per shot (1 for single-shot, 6 for shotgun) */
  readonly pelletsPerShot: number;
}

// ============================================================================
// WEAPON CONFIGURATIONS
// ============================================================================

/**
 * Default weapon configuration
 * Role: Balanced all-rounder for general combat
 * DPS: 10 damage × 1000ms/shot = 10 DPS
 * Strengths: Reliable, precise, no weaknesses
 * Weaknesses: No particular advantages
 */
const DEFAULT_WEAPON_CONFIG: WeaponStats = {
  damage: 10,
  fireRateMs: 1000,
  bulletSpeed: 500,
  bulletLifetime: 3.0,
  spreadDegrees: 0,
  pelletsPerShot: 1,
};

/**
 * Shotgun weapon configuration
 * Role: Close-range burst damage specialist
 * DPS: (3 damage × 6 pellets) × 1000ms/1500ms = 12 DPS
 * Strengths: High burst damage, wide coverage
 * Weaknesses: Short range, slow reload, poor precision
 */
const SHOTGUN_WEAPON_CONFIG: WeaponStats = {
  damage: 3,
  fireRateMs: 1500,
  bulletSpeed: 400,
  bulletLifetime: 1.5,
  spreadDegrees: 15,
  pelletsPerShot: 6,
};

/**
 * Sniper weapon configuration
 * Role: Long-range precision eliminations
 * DPS: 25 damage × 1000ms/2000ms = 12.5 DPS
 * Strengths: High damage per shot, maximum range, perfect accuracy
 * Weaknesses: Slow fire rate, punishing misses
 */
const SNIPER_WEAPON_CONFIG: WeaponStats = {
  damage: 25,
  fireRateMs: 2000,
  bulletSpeed: 2400,
  bulletLifetime: 4.0,
  spreadDegrees: 0,
  pelletsPerShot: 1,
};

/**
 * Machine Gun weapon configuration
 * Role: Sustained DPS and area suppression
 * DPS: 1 damage × 1000ms/200ms = 5 DPS
 * Strengths: High fire rate (300 RPM), bullet volume compensates for spread
 * Weaknesses: Low damage per shot, 5° spread = only ~3.6% hit rate at medium range
 * Philosophy: Spray-and-pray suppressive fire, effective DPS ~0.18 at medium range
 */
const MACHINEGUN_WEAPON_CONFIG: WeaponStats = {
  damage: 1,
  fireRateMs: 200,
  bulletSpeed: 450,
  bulletLifetime: 2.5,
  spreadDegrees: 5,
  pelletsPerShot: 1,
};

/**
 * Weapon configurations mapped by weapon type
 * Immutable record for safe access from game logic
 */
export const WEAPON_CONFIGS: Readonly<Record<WeaponType, WeaponStats>> = {
  [WeaponType.DEFAULT]: DEFAULT_WEAPON_CONFIG,
  [WeaponType.SHOTGUN]: SHOTGUN_WEAPON_CONFIG,
  [WeaponType.SNIPER]: SNIPER_WEAPON_CONFIG,
  [WeaponType.MACHINEGUN]: MACHINEGUN_WEAPON_CONFIG,
};

// ============================================================================
// WEAPON DISPLAY CONFIGURATION
// ============================================================================

/**
 * Human-readable weapon names for UI display
 */
const WEAPON_DISPLAY_NAMES: Readonly<Record<WeaponType, string>> = {
  [WeaponType.DEFAULT]: "Default",
  [WeaponType.SHOTGUN]: "Shotgun",
  [WeaponType.SNIPER]: "Sniper",
  [WeaponType.MACHINEGUN]: "Machine Gun",
};

/**
 * Weapon role descriptions for UI tooltips
 */
const WEAPON_DESCRIPTIONS: Readonly<Record<WeaponType, string>> = {
  [WeaponType.DEFAULT]: "Balanced all-rounder",
  [WeaponType.SHOTGUN]: "Close-range burst damage",
  [WeaponType.SNIPER]: "Long-range precision",
  [WeaponType.MACHINEGUN]: "Sustained DPS",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get weapon configuration for a given weapon type
 * Falls back to DEFAULT if invalid type provided
 *
 * @param weaponType - The weapon type to retrieve configuration for
 * @returns Weapon configuration object
 *
 * @example
 * ```ts
 * const sniperConfig = getWeaponConfig(WeaponType.SNIPER);
 * console.log(sniperConfig.damage); // 25
 * ```
 */
export function getWeaponConfig(weaponType: WeaponType): WeaponStats {
  return WEAPON_CONFIGS[weaponType] ?? WEAPON_CONFIGS[WeaponType.DEFAULT];
}

/**
 * Validate if a string represents a valid weapon type
 * Useful for parsing user input or network messages
 *
 * @param weaponType - String to validate as weapon type
 * @returns True if valid weapon type, false otherwise
 *
 * @example
 * ```ts
 * isValidWeaponType("SHOTGUN"); // true
 * isValidWeaponType("LASER");   // false
 * ```
 */
export function isValidWeaponType(
  weaponType: string,
): weaponType is WeaponType {
  return Object.values(WeaponType).includes(weaponType as WeaponType);
}

/**
 * Get human-readable weapon name for UI display
 *
 * @param weaponType - The weapon type to get display name for
 * @returns Formatted weapon name
 *
 * @example
 * ```ts
 * getWeaponDisplayName(WeaponType.MACHINEGUN); // "Machine Gun"
 * ```
 */
export function getWeaponDisplayName(weaponType: WeaponType): string {
  return (
    WEAPON_DISPLAY_NAMES[weaponType] ?? WEAPON_DISPLAY_NAMES[WeaponType.DEFAULT]
  );
}

/**
 * Formatted weapon statistics for UI display
 * Includes computed values like DPS and effective range
 */
export interface WeaponDisplayStats {
  /** Weapon name for display */
  readonly name: string;
  /** Weapon role description */
  readonly description: string;
  /** Damage per bullet */
  readonly damage: number;
  /** Fire rate in rounds per minute */
  readonly fireRateRPM: number;
  /** Theoretical DPS (damage × fire rate, not accounting for misses) */
  readonly dps: number;
  /** Bullet speed in px/s */
  readonly bulletSpeed: number;
  /** Maximum effective range in pixels */
  readonly maxRange: number;
  /** Spread angle in degrees */
  readonly spread: number;
  /** Bullets per shot */
  readonly pellets: number;
}

/**
 * Get formatted weapon statistics for UI display
 * Computes derived values like DPS and effective range
 *
 * @param weaponType - The weapon type to get stats for
 * @returns Formatted weapon statistics object
 *
 * @example
 * ```ts
 * const stats = getWeaponStats(WeaponType.SHOTGUN);
 * console.log(stats.dps);      // 12
 * console.log(stats.maxRange); // 600
 * ```
 */
export function getWeaponStats(weaponType: WeaponType): WeaponDisplayStats {
  const config = getWeaponConfig(weaponType);
  const shotsPerSecond = 1000 / config.fireRateMs;
  const damagePerShot = config.damage * config.pelletsPerShot;
  const dps = damagePerShot * shotsPerSecond;
  const maxRange = config.bulletSpeed * config.bulletLifetime;
  const fireRateRPM = Math.round(shotsPerSecond * 60);

  return {
    name: getWeaponDisplayName(weaponType),
    description: WEAPON_DESCRIPTIONS[weaponType] ?? "",
    damage: config.damage,
    fireRateRPM,
    dps: Math.round(dps * 10) / 10, // Round to 1 decimal place
    bulletSpeed: config.bulletSpeed,
    maxRange: Math.round(maxRange),
    spread: config.spreadDegrees,
    pellets: config.pelletsPerShot,
  };
}

/**
 * Calculate effective bullet spread at target distance
 * Useful for hit probability calculations
 *
 * @param weaponType - The weapon type
 * @param distance - Distance to target in pixels
 * @returns Spread radius at target distance in pixels
 *
 * @example
 * ```ts
 * const spread = calculateSpreadAtDistance(WeaponType.SHOTGUN, 200);
 * console.log(spread); // ~52.4 pixels (15° spread at 200px)
 * ```
 */
export function calculateSpreadAtDistance(
  weaponType: WeaponType,
  distance: number,
): number {
  const config = getWeaponConfig(weaponType);
  if (config.spreadDegrees === 0) {
    return 0;
  }

  // Convert degrees to radians, calculate spread radius at distance
  const spreadRadians = (config.spreadDegrees * Math.PI) / 180;
  return Math.tan(spreadRadians) * distance;
}

/**
 * Calculates dynamic shotgun spread based on cursor distance.
 * Linearly interpolates between wide spread up close and tight spread far away.
 *
 * Distance mapping (pixels → degrees):
 * - <= 100px ⇒ 55°
 * - >= 400px ⇒ 15°
 * - between ⇒ linear interpolation from 55° → 15°
 *
 * @param cursorDistance - Pixel distance from player to cursor
 * @returns Spread angle in degrees for this shot
 */
export function calculateShotgunSpread(cursorDistance: number): number {
  const MIN_DIST = 100;
  const MAX_DIST = 400;
  const MAX_SPREAD = 55; // degrees at close range
  const MIN_SPREAD = 15; // degrees at long range

  if (!Number.isFinite(cursorDistance) || cursorDistance <= MIN_DIST) {
    return MAX_SPREAD;
  }
  if (cursorDistance >= MAX_DIST) {
    return MIN_SPREAD;
  }

  const t = (cursorDistance - MIN_DIST) / (MAX_DIST - MIN_DIST);
  return MAX_SPREAD + t * (MIN_SPREAD - MAX_SPREAD);
}
